import { useMutation } from 'react-query';
import { shallow } from 'zustand/shallow';
import usePlanStore from '../../../../../../store/planStore';
import { getMetaConfig } from '../../../../../../api/services/mediaPlanning/touchpointConfigs';
import { useError } from '../../../common/Errors/ErrorContext/ErrorContext';
import { Item } from '../../../common/DefaultSelect/DefaultSelect';

const useMetaConfig = () => {
  const {
    selectedCountry,
    selectedCountryISOCode,
    countryForms,
    generalInfoState,
    updateMetaConfig,
  } = usePlanStore();

  const { showError } = useError();

  const selectedCountryForm = countryForms[selectedCountry!];
  const metaConfig = selectedCountryForm?.metaConfig;

  const selectedCountryFormSubset = usePlanStore((state) => {
    const form = state.savedCountryForms[state.selectedCountry!];
    return form
      ? {
          targetGroup: form.targetGroup,
          campaignScenario: form.campaignScenario,
          segments: form.segments,
          startDate: form.startDate,
          campaignLength: form.campaignLength,
          isCountrySetupConfirmed: form.isCountrySetupConfirmed,
        }
      : null;
  }, shallow);

  const {
    mutate: fetchMetaConfig,
    isLoading,
    data,
    error,
  } = useMutation({
    mutationFn: (metaTargetGroup?: Item) => {
      if (
        !generalInfoState.division ||
        !selectedCountry ||
        !selectedCountryISOCode ||
        !countryForms[selectedCountry]?.campaignScenario
      ) {
        return Promise.reject(
          new Error('Missing required parameters for fetching meta config.')
        );
      }

      return getMetaConfig(
        generalInfoState.division,
        generalInfoState.brand,
        generalInfoState.category,
        selectedCountryISOCode,
        countryForms[selectedCountry].targetGroup,
        countryForms[selectedCountry].campaignScenario,
        metaTargetGroup
          ? String(metaTargetGroup.value)
          : metaConfig?.targetGroup,
        metaConfig?.startDate,
        metaConfig?.campaignLength
      );
    },
    onError: (err: any) => {
      const errorMessage =
        err?.response?.data?.message || err.message || 'Unknown error';
      showError(errorMessage);
    },
    onSuccess: (response, metaTargetGroup) => {
      if (selectedCountry) {
        updateMetaConfig(selectedCountry, {
          saturation: response?.data?.saturationDefault || undefined,
          partnerBudgetShareConversion: 0,
          partnerBudgetShareConsideration: 0,
          metaConverts: false,
          campaignLength: countryForms[selectedCountry].campaignLength,
          partners: [],
          targetGroup: metaTargetGroup?.value
            ? String(metaTargetGroup.value)
            : response?.data?.recommendedTargetGroup ?? null,
          targetGroups: response?.data?.targetGroups ?? [],
          startDate:
            response?.data?.startDate ??
            countryForms[selectedCountry].startDate,
          isGoldenRuleMet: response?.data?.isGoldenRuleMet,
          saturationMin: response?.data?.saturationMin ?? null,
          saturationMax: response?.data?.saturationMax ?? null,
          saturationDefault: response?.data?.saturationDefault ?? null,
          reachEstimateMin: response.data.reachEstimateMin ?? null,
          recommendedTargetGroup: response.data.recommendedTargetGroup ?? null,
          metaMinReachFactor: response.data.metaMinReachFactor ?? null,
          isMaxSaturationExtended:
            response.data.isMaxSaturationExtended ?? false,
          metaAccountId: response.data.metaAccountId ?? '',
        });
      }
    },
  });

  return {
    fetchMetaConfig,
    isFetching: isLoading,
    data,
    error,
  };
};

export default useMetaConfig;
